import {
  tenantDaysAgoISO,
  tenantFirstOfMonthISO,
  tenantTodayISO,
} from "@/lib/date";
import type { UrlFilterValues, UrlFiltersConfig } from "@/hooks/useUrlFilters";

/**
 * Non-component module holding the URL-filter config blocks and tab metadata for
 * the consolidated Analytics page (Sprint 117, TASK-3828). Kept separate from
 * the tab components so each component file exports only components (satisfies
 * the react-refresh/only-export-components lint rule).
 */

/** Canonical ordered set of analytics tab values (drives `?tab=`). */
export const ANALYTICS_TABS = [
  "dashboard",
  "plan-vs-actual",
  "drivers",
  "reviews",
  "fleet-load",
  "notifications",
  "reports",
] as const;

export type AnalyticsTab = (typeof ANALYTICS_TABS)[number];

/** How many days back the analytics default window reaches. */
export const ANALYTICS_DEFAULT_SPAN_DAYS = 30;

/**
 * The analytics default date window — the last 30 days in the TENANT's zone
 * (parity with the former Reports/Feedback pages).
 *
 * TASK-5041: these were module-level CONSTANTS
 *
 *   export const ANALYTICS_DEFAULT_DATE_FROM = daysAgoStr(30);
 *   export const ANALYTICS_DEFAULT_DATE_TO = todayStr();
 *
 * carrying two defects at once — the same pair `routesDateWindow.ts:6-23`
 * documents and fixes for the routes list:
 *
 *   1. `todayStr()` sliced a UTC instant and `daysAgoStr()` mixed browser-LOCAL
 *      day arithmetic (`d.setDate(d.getDate() - days)`) with that UTC slice
 *      inside one four-line function — two zones, neither of them the tenant's.
 *      For Europe/Minsk (UTC+3) the window named YESTERDAY between 00:00 and
 *      03:00 local.
 *   2. Being module scope, both were evaluated ONCE at import, so a dispatcher
 *      who left the tab open across midnight kept filtering the previous day
 *      until a full reload.
 *
 * Functions, not constants, fix (2); taking the zone as a PARAMETER fixes (1)
 * without making this component-free module depend on a hook
 * (`DraggableStopList.tsx:59-63` is the precedent for passing the zone in).
 */
export function analyticsDefaultDateTo(tz: string): string {
  return tenantTodayISO(tz);
}

export function analyticsDefaultDateFrom(tz: string): string {
  return tenantDaysAgoISO(tz, ANALYTICS_DEFAULT_SPAN_DAYS);
}

/** The quick-range preset pills the shared analytics bar renders. */
export const ANALYTICS_PRESETS = [
  "today",
  "days7",
  "days30",
  "thisMonth",
] as const;

export type AnalyticsPresetKey = (typeof ANALYTICS_PRESETS)[number];

/**
 * The `{date_from, date_to}` range a quick-range preset pill selects, in the
 * TENANT's zone.
 *
 * TASK-5041: `AnalyticsFilterBar` carried its own `todayStr`/`daysAgoStr`/
 * `firstOfMonthStr` triple — a near-copy of the one in this module, and each
 * function mixed browser-LOCAL day arithmetic with a UTC slice. The resolution
 * is here, in the one module that already owned the analytics date defaults, so
 * a pill and the page's default window can no longer disagree about which day
 * «today» is. The zone is a PARAMETER: this module exports no components and
 * must not depend on a hook (`DraggableStopList.tsx:59-63` is the precedent).
 */
export function analyticsPresetRange(
  preset: AnalyticsPresetKey,
  tz: string,
): { date_from: string; date_to: string } {
  const today = tenantTodayISO(tz);
  switch (preset) {
    case "today":
      return { date_from: today, date_to: today };
    case "days7":
      return { date_from: tenantDaysAgoISO(tz, 7), date_to: today };
    case "days30":
      return {
        date_from: tenantDaysAgoISO(tz, ANALYTICS_DEFAULT_SPAN_DAYS),
        date_to: today,
      };
    case "thisMonth":
      return { date_from: tenantFirstOfMonthISO(tz), date_to: today };
  }
}

/**
 * Url-filter config block for the Reviews tab, merged into the shell config.
 * The Analytics shell owns the {@link useUrlFilters} instance and passes the
 * slice down so all tabs share one query string. `satisfies UrlFiltersConfig`
 * keeps the literal key types while meeting the hook's index-signature
 * constraint, so {@link ReviewsValues} stays precisely typed.
 *
 * A function of the tenant zone (TASK-5041) for the same reason the defaults
 * above are: the date bounds inside it must be the tenant's current day,
 * resolved when the config is built rather than when the module is imported.
 */
export function reviewsFiltersConfig(tz: string) {
  return {
    rating: { defaultValue: "" },
    date_from: { defaultValue: analyticsDefaultDateFrom(tz) },
    date_to: { defaultValue: analyticsDefaultDateTo(tz) },
    driver_id: { defaultValue: "" },
    has_comment: { defaultValue: "" },
    search: { defaultValue: "" },
    // Active quick-range preset pill — shared with the analytics filter bar so
    // the Reviews tab can render the same date-range presets as the rest of the
    // page.
    preset: { defaultValue: "days30" },
    page: { defaultValue: "1" },
  } satisfies UrlFiltersConfig;
}

export type ReviewsTabConfig = ReturnType<typeof reviewsFiltersConfig>;
export type ReviewsValues = UrlFilterValues<ReviewsTabConfig>;

/**
 * Url-filter config block for the Notifications tab, merged into the shell.
 * Namespaced (`n_*`) so the params never collide with the Reviews tab's own
 * `search`/`page` params on the shared query string.
 */
export const NOTIFICATIONS_FILTERS_CONFIG = {
  channel: { defaultValue: "" },
  status: { defaultValue: "" },
  undelivered: { defaultValue: "" },
  n_search: { defaultValue: "" },
  n_page: { defaultValue: "1" },
} satisfies UrlFiltersConfig;

export type NotificationsTabConfig = typeof NOTIFICATIONS_FILTERS_CONFIG;
export type NotificationsValues = UrlFilterValues<NotificationsTabConfig>;
